// Utility functions for dayId/date string conversions

/**
 * Converts a dayId like 'day_2025_05_29' to '2025-05-29'.
 */
export function dayIdToDateString(dayId: string): string {
    const match = dayId.match(/^day_(\d{4})_(\d{2})_(\d{2})$/);
    if (!match) return '';
    const [, year, month, day] = match;
    return `${year}-${month}-${day}`;
}

/**
 * Converts a date string like '2025-05-29' to 'day_2025_05_29'.
 */
export function dateStringToDayId(dateString: string): string {
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    const [, year, month, day] = match;
    return `day_${year}_${month}_${day}`;
}

/** Compares value to compareTo and if they match OR value is undefined, returns null. otherwise returns value. */
export function nullIf<T>(value: T | null | undefined, compareTo: T): T | null {
    if (value === compareTo || value == undefined){
        return null;
    }
    return value;
}

/** Returns first non-null / non-undefined value. returns null if none found. never returns undefined. */
export function coalesce<T>(...values: (T | null | undefined)[]): T | null {
  for (const val of values) {
    if (val !== null && val !== undefined) {
      return val;
    }
  }
  return null;
}

/**
 * Utility functions for encoding/decoding strings to be used as Firebase property keys
 * This ensures consistent and safe handling of special characters in property names
 */

/**
 * Encodes a string to be used as a Firebase property key
 * @param key - The string to encode
 * @param prefix - Optional prefix to prepend to the encoded key
 * @returns The encoded string safe for use as a Firebase property key
 */
export function encodeKey(key: string, prefix?: string): string {
    if (!key) return '';

    // First convert to lowercase for consistency
    const normalized: string = key.toLowerCase();

    // Replace common problematic characters with safe alternatives
    const encoded = normalized
        .replace(/\./g, '_dot_')
        .replace(/@/g, '_at_')
        .replace(/\+/g, '_plus_')
        .replace(/\$/g, '_dollar_')
        .replace(/#/g, '_hash_')
        .replace(/\[/g, '_lbracket_')
        .replace(/\]/g, '_rbracket_')
        .replace(/\//g, '_slash_')
        .replace(/\\/g, '_backslash_')
        .replace(/\s+/g, '_space_')
        .replace(/-/g, '_hyphen_')
        .replace(/\?/g, '_question_')
        .replace(/!/g, '_exclamation_')
        .replace(/%/g, '_percent_')
        .replace(/\^/g, '_caret_')
        .replace(/&/g, '_ampersand_')
        .replace(/\*/g, '_asterisk_')
        .replace(/\(/g, '_lparen_')
        .replace(/\)/g, '_rparen_')
        .replace(/\{/g, '_lbrace_')
        .replace(/\}/g, '_rbrace_')
        .replace(/\|/g, '_pipe_')
        .replace(/,/g, '_comma_')
        .replace(/;/g, '_semicolon_')
        .replace(/:/g, '_colon_')
        .replace(/'/g, '_squote_')
        .replace(/"/g, '_dquote_')
        .replace(/</g, '_lt_')
        .replace(/>/g, '_gt_')
        .replace(/~/g, '_tilde_')
        .replace(/=/g, '_equals_')
        // Replace any remaining non-alphanumeric characters with underscore
        .replace(/[^a-z0-9_]/g, '_');

    return prefix ? `${prefix}${encoded}` : encoded;
}

/**
 * Decodes an encoded key back to its original string
 * @param encodedKey - The encoded key
 * @param prefix - Optional prefix that was used in encoding
 * @returns The original string
 */
export function decodeKey(encodedKey: string, prefix?: string): string {
    if (!encodedKey) return '';

    // Remove prefix if it exists
    const keyWithoutPrefix = prefix && encodedKey.startsWith(prefix)
        ? encodedKey.slice(prefix.length)
        : encodedKey;

    return keyWithoutPrefix
        .replace(/_dot_/g, '.')
        .replace(/_at_/g, '@')
        .replace(/_plus_/g, '+')
        .replace(/_dollar_/g, '$')
        .replace(/_hash_/g, '#')
        .replace(/_lbracket_/g, '[')
        .replace(/_rbracket_/g, ']')
        .replace(/_slash_/g, '/')
        .replace(/_backslash_/g, '\\')
        .replace(/_space_/g, ' ')
        .replace(/_hyphen_/g, '-')
        .replace(/_question_/g, '?')
        .replace(/_exclamation_/g, '!')
        .replace(/_percent_/g, '%')
        .replace(/_caret_/g, '^')
        .replace(/_ampersand_/g, '&')
        .replace(/_asterisk_/g, '*')
        .replace(/_lparen_/g, '(')
        .replace(/_rparen_/g, ')')
        .replace(/_lbrace_/g, '{')
        .replace(/_rbrace_/g, '}')
        .replace(/_pipe_/g, '|')
        .replace(/_comma_/g, ',')
        .replace(/_semicolon_/g, ';')
        .replace(/_colon_/g, ':')
        .replace(/_squote_/g, "'")
        .replace(/_dquote_/g, '"')
        .replace(/_lt_/g, '<')
        .replace(/_gt_/g, '>')
        .replace(/_tilde_/g, '~')
        .replace(/_equals_/g, '=');
}

/**
 * Type for a function that can encode/decode keys
 */
export type KeyEncoder = (key: string, prefix?: string) => string;

/**
 * Example usage:
 * const original = 'user.name+tag@example.com';
 * const encoded = encodeKey(original);
 * // encoded will be: 'user_dot_name_plus_tag_at_example_dot_com'
 * 
 * const decoded = decodeKey(encoded);
 * // decoded will be: 'user.name+tag@example.com'
 * 
 * // Can also be used for other types of keys:
 * const pathKey = 'users/123/profile#settings';
 * const safeKey = encodeKey(pathKey);
 * // safeKey will be: 'users_slash_123_slash_profile_hash_settings'
 * 
 * // Example with special characters:
 * const specialKey = "user's name, email; test@example.com";
 * const safeSpecialKey = encodeKey(specialKey);
 * // safeSpecialKey will be: 'user_squote_s_space_name_comma_space_email_semicolon_space_test_at_example_dot_com'
 * 
 * // Example with all special characters:
 * const allSpecial = '~!@#$%^&*()_+-=[]{}\\|;\':",./<>?';
 * const encodedAll = encodeKey(allSpecial);
 * // encodedAll will be: 'tilde_exclamation_at_hash_dollar_percent_caret_ampersand_asterisk_lparen_rparen_underscore_plus_hyphen_equals_lbracket_rbracket_lbrace_rbrace_backslash_pipe_semicolon_squote_dquote_comma_dot_slash_lt_gt_question'
 */ 